export const error = {
  title: 'Oops! Something went wrong.',
  body: 'Please try again later',
  button: 'Done',
};

const lateFeeCaption =
  'If a payment is late, a fee of up to $5.00 may apply. Late fees will never exceed 25% of your purchase total.';

export default {
  manageLoans: {
    title: 'Pay Over Time',
    totalOwed: 'Outstanding balance',
    payOverTimePlans: 'Active plans',
    emptyHistory: 'Your past loan activity will be shown here.',
    error: {
      ...error,
    },
  },
  loanDetails: {
    amountDue: 'Amount due',
    nextPaymentDue: 'Next payment date',
    autopay: 'Autopay',
    autoPayStatus: {
      on: 'On',
      off: 'Off',
    },
    makePayment: 'Make a payment',
    remainingBalance: 'Remaining balance',
    paymentSchedule: 'Payment schedule',
    emptyState: 'Your payment activity will be shown here.',
    autopayBottomSheet: {
      illustrationAccessibilityLabel: 'Autopay switch',
      title: 'Never miss a payment',
      body: 'Turning on autopay helps you stay consistent with your loan payments. It’s secure, automatic, and designed for peace of mind.',
      turnOnAutopay: 'Turn on autopay',
      notRightNow: 'Not right now',
    },
    error: {
      ...error,
    },
  },
  planDetails: {
    planDetails: 'Plan details',
    planAmount: 'Plan amount',
    fee: 'Fee',
    total: 'Total',
    planDate: 'Plan date',
    error: {
      ...error,
    },
  },
  accountSettings: {
    screenTitle: 'Account settings',
    manageAccountHeader: 'Manage account',
    statementsAndDocuments: 'Statements and documents',
    helpSectionHeader: 'Help',
    payOverTimeFaqs: 'Pay Over Time FAQs',
  },
  paymentSchedule: {
    paidToDate: 'Paid to date',
    remainingBalance: 'Remaining balance',
    header: 'Payment schedule',
    due: 'due',
    lateFeeCaption,
    ACTIVE: 'Payment due',
    DELINQUENT: 'Payment overdue',
    PAID: 'Paid',
    REVERSED: 'Reversed',
    UNDEFINED_INSTALLMENT_STATE: 'Undefined',
  },
  autopayScreen: {
    header: 'Autopay',
    onTitle: 'AutoPay is on',
    onBody: 'AutoPay is on. Your payment plan will be paid on time.',
    offTitle: 'AutoPay is off',
    offBody:
      'AutoPay is off. Turn it on to automatically pay your plan on time.',
    onButton: 'Turn on AutoPay',
    offButton: 'Turn off AutoPay',
    error: {
      button: error.button,
    },
  },
  manualRepay: {
    title: 'Pay loan',
    selectAnAccount: 'Select an account',
    from: 'From: ',
    available: 'Available:',
    buttons: {
      next: 'Next',
      done: 'Done',
    },
    error: {
      button: error.button,
    },
    confirmPayment: 'Confirm payment',
    repayFrom: 'Repay from',
    paymentAmount: 'Payment amount',
    paymentSchedule: 'Payment schedule',
    paymentFrom: 'Payment from',
    overPayment: 'Maximum payment is ${{amount}}',
    partialPayment: 'You will owe ${{remainingAmount}} on {{dueDate}}',
    belowMinimumPayment: 'Minimum payment is ${{minimumPayment}}',
    lowBalance: 'Your limit for this payment is ${{amount}}',
    autopayNotice:
      'This one-time payment does not replace your next AutoPay payment. Your next Autopay will be taken from your Varo account on {{date}}.',
  },
  applyNow: {
    navigationTitle: 'Pay Over Time',
    screenTitle: 'Pay off purchases over time',
    subTitle:
      'Choose eligible past transactions to split into equal payments with Pay Over Time for one flat fee.',
    bullet1: 'Applying won’t impact your credit',
    bullet2: 'Split a past purchase into equal payments',
    bullet3: 'One-time fee per transaction',
    footer: 'Qualifications apply.',
    button: 'Apply now',
    landingPageButton: 'Get started',
    noAccountError: {
      title: "Pay Over Time isn't available yet",
      body: 'Pay Over Time splits purchases made with your Varo Bank Account debit card into equal payments. You’ll need a Varo Bank Account to use it.',
    },
    missingTransactionError: {
      title: "We can't start your application",
      body: 'Open the purchase you want to split from your account, then tap Pay Over Time on that transaction.',
    },
    done: 'Done',
  },
  applicationDecline: {
    title: 'Your application was not approved',
    body: "Thank you for applying for Pay Over Time. Unfortunately, your application can't be approved at this time.",
    followUp: "We'll send a follow up email with more information.",
    button: 'Done',
  },
  applicationSuccess: {
    title: "You're all set",
    bulletOne: '{{amount}} is back in your Varo Bank Account',
    bulletTwo: 'Your first payment of {{amount}} is due on {{date}}',
    button: 'Done',
    autopayError: {
      unconfirmedOn: {
        title: 'Check your autopay setting',
        body: "We couldn't confirm autopay is on for this plan. If it's off, you'll need to make each payment manually to avoid a late fee. Check your autopay setting in your Pay Over Time plan details.",
      },
      unconfirmedOff: {
        title: 'Check your autopay setting',
        body: "We couldn't confirm autopay is off for this plan. If it's on, payments will be deducted automatically from your Varo Bank Account on each due date. Check your autopay setting in your Pay Over Time plan details.",
      },
    },
  },
  listItem: {
    title: 'Pay Over Time',
    secondaryCopy: 'Split this purchase into equal payments for a flat fee.',
    activeTag: 'Active',
    closedTitle: 'Pay Over Time history',
  },
  applyNowBottomSheet: {
    title: 'Split this purchase into equal payments',
    body: "Apply now—you'll review your payment plan and schedule before anything is final.",
    viewPaymentPlan: 'View payment plan',
    close: 'Close',
  },
  transactionDetails: {
    header: 'Transaction details',
    description: 'Description',
    requestedOn: 'Requested on',
    status: 'Status',
  },
  paymentPlan: {
    title: 'Review your payment plan',
    paymentSchedule: 'Payment schedule',
    paymentDetails: 'Payment details',
    available: 'Available:',
    autopayLabel: 'Autopay',
    autopayDescription:
      'With Autopay “On,” you authorize Varo to automatically make payments from your Varo Bank Account on their due dates. Autopay is optional. You can turn it off at any time.',
    lateFeeCaption,
    whatIsAutopay: 'What is autopay?',
    whatIsAutopayBottomSheet: {
      title: 'AutoPay',
      body: 'When AutoPay is on, your payment will be deducted automatically from your Varo Bank Account on your payment due date. If AutoPay is off, you will need to make payments manually by your payment due date to remain in good standing.',
      gotIt: 'Got it',
    },
    agreeToTerms: 'I agree and acknowledge receipt of the',
    truthInLendingAndAgreement:
      'Pay Over Time Truth in Lending Disclosure and Agreement',
    acceptanceWarning:
      'Once you tap “Accept plan” you can’t change or cancel your Pay Over Time amount, so review carefully. It’s important to make payments on-time.',
    loadingBody: 'Stay on this page and give us just a moment to wrap up.',
    acceptPlan: 'Accept plan',
    cancel: 'Cancel',
    error: {
      ...error,
    },
  },
  truthInLending: {
    share: 'Share',
    shareDialogTitle: 'Save your Truth in Lending Disclosure',
    shareError: "We couldn't download your disclosure. Please try again.",
    retry: 'Retry',
  },
};
